import type { Board, BoardEdge } from '../protocol/ui';
import type { BoardVisual } from './types';

export type EdgePoint = [number, number];
/** Geometry only: retains one edge identity and never connects separate wrap parts. */
export function edgeGeometry(board: Board, edge: BoardEdge, visual?: BoardVisual | null, offset = 0) {
  const a = board.nodes.find(n => n.id === edge.a)!, b = board.nodes.find(n => n.id === edge.b)!;
  const length = Math.hypot(b.x-a.x,b.y-a.y) || 1;
  const dx = -(b.y-a.y)/length*offset, dy = (b.x-a.x)/length*offset;
  const custom = visual?.paths?.find(p => p.edge === edge.id);
  const parts: EdgePoint[][] = custom?.parts ?? [[[a.x+dx,a.y+dy],[b.x+dx,b.y+dy]]];
  const measured = parts.map(points => {
    const segments = points.slice(1).map((p,i) => ({ a: points[i]!, b: p, length: Math.hypot(p[0]-points[i]![0],p[1]-points[i]![1]) }));
    return { points, segments, length: segments.reduce((n,s) => n+s.length,0) };
  });
  const longest = measured.reduce((a,b) => b.length > a.length ? b : a);
  const segment = longest.segments.reduce((a,b) => b.length > a.length ? b : a);
  let left = longest.length/2, midpoint: EdgePoint = longest.points[0]!;
  for (const s of longest.segments) {
    if (left <= s.length) { const k = s.length ? left/s.length : 0; midpoint = [s.a[0]+(s.b[0]-s.a[0])*k,s.a[1]+(s.b[1]-s.a[1])*k]; break; }
    left -= s.length;
  }
  // Describe permits distinct nodes at one coordinate. Keep the legacy nonzero
  // display length so fitted faces and target floors preserve their defaults.
  return { parts, midpoint, length: custom ? measured.reduce((n,p) => n+p.length,0) || 1 : length,
    face: { x1: segment.a[0], y1: segment.a[1], x2: segment.b[0], y2: segment.b[1], length: segment.length || 1,
      angle: Math.atan2(segment.b[1]-segment.a[1],segment.b[0]-segment.a[0])*180/Math.PI } };
}
